import axios from '@/libs/axios';
import Swal from 'sweetalert2';
import { isPerfilClienteAtivo } from '@/libs/perfilCliente';

const TIPOS_CLIENTE = ['WS', 'WC'];

function normalizarTipo(value) {
  const tipo = String(value || '').trim().toUpperCase();
  return TIPOS_CLIENTE.includes(tipo) ? tipo : '';
}

function categoriaDasFlags(flags) {
  const normalizadas = Array.isArray(flags)
    ? flags.map((flag) => normalizarTipo(flag)).filter(Boolean)
    : [];
  // Certificação prevalece quando o ERP também retorna a flag vendável.
  if (normalizadas.includes('WC')) return 'WC';
  if (normalizadas.includes('WS')) return 'WS';
  return '';
}

function categoriaDaLinha(item) {
  return normalizarTipo(
    item?.categoria_tipo_pedido
      || item?.categoriaTipoPedido
      || item?.tipopedidoid,
  );
}

export {
  categoriaDasFlags,
  categoriaDaLinha,
  normalizarTipo,
};

export default {
  data() {
    return {
      categoriaClienteReservada: '',
    };
  },
  methods: {
    categoriaClienteAtiva() {
      return isPerfilClienteAtivo();
    },
    /**
     * O modal de edição sobrescreve estes dois hooks. Assim a categoria da capa
     * persistida continua sendo a fonte de verdade mesmo com a seleção vazia.
     */
    categoriaPedidoPersistida() {
      return '';
    },
    itensPedidoPersistidosCategoria() {
      return this.getlistarTabelaItens || [];
    },
    _linhasCategoriaPedido() {
      const achatar = (lista) => (lista || []).flatMap(
        (linha) => (Array.isArray(linha?.itens) ? linha.itens : [linha]),
      ).filter(Boolean);
      return [
        ...achatar(this.itensPedidoPersistidosCategoria()),
        ...achatar(this.itensTabela),
      ];
    },
    _tipoAtualInfoPedido() {
      const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
      return normalizarTipo(
        form.tipoPedido?.tipopedidoid
          || form.tipoPedidoCliente?.tipopedidoid
          || this.$store.getters['intensPedido/getFormData']?.dataCapaPedido?.tipopedidoid,
      );
    },
    categoriaPedidoBloqueada() {
      const persistida = normalizarTipo(this.categoriaPedidoPersistida());
      if (persistida) return persistida;
      const reservada = normalizarTipo(this.categoriaClienteReservada);
      if (reservada) return reservada;

      const linhas = this._linhasCategoriaPedido();
      const categoriaLinha = linhas.map(categoriaDaLinha).find(Boolean);
      if (categoriaLinha) return categoriaLinha;

      // O WS default só passa a ser trava se já existe item no pedido. Em um
      // pedido novo realmente vazio, o primeiro SKU (inclusive WC) define o tipo.
      return linhas.length ? this._tipoAtualInfoPedido() : '';
    },
    clienteIdCategoria() {
      const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
      return form?.cliente?.clienteid
        || form?.cliente?.clienteiderp
        || this.getCapaPedido?.clienteid
        || this.getCapaPedido?.clienteiderp
        || null;
    },
    async identificarCategoriaItemCliente(item) {
      if (!this.categoriaClienteAtiva()) return { ...item };

      const itemId = item?.item_id || item?.itemId;
      if (!itemId) {
        await Swal.fire({
          icon: 'warning',
          title: 'Categoria não identificada',
          text: 'Não foi possível identificar a categoria deste produto.',
        });
        return null;
      }

      let flags;
      try {
        const { data } = await axios.post('items/categorias', {
          itemIds: [itemId],
          clienteid: this.clienteIdCategoria(),
        });
        flags = data?.flags?.[itemId];
      } catch (error) {
        console.warn('[Operação] categorias do item indisponíveis:', error?.message);
        await Swal.fire({
          icon: 'warning',
          title: 'Categoria indisponível',
          text: 'Não foi possível confirmar se o produto é Padrão ou Certificação. Tente novamente.',
        });
        return null;
      }

      const categoria = categoriaDasFlags(flags);
      if (!categoria) {
        await Swal.fire({
          icon: 'warning',
          title: 'Categoria não identificada',
          text: 'Este produto não possui categoria Padrão ou Certificação confirmada e não pode ser adicionado.',
        });
        return null;
      }

      const bloqueada = this.categoriaPedidoBloqueada();
      if (bloqueada && bloqueada !== categoria) {
        await Swal.fire({
          icon: 'warning',
          title: 'Categoria diferente',
          text: `O produto "${item.nome || item.item_descricao || itemId}" é da categoria ${categoria === 'WC' ? 'Certificação' : 'Padrão'}, `
            + `diferente da categoria deste pedido (${bloqueada === 'WC' ? 'Certificação' : 'Padrão'}). `
            + 'Finalize este pedido e faça um novo pedido da categoria correta para incluí-lo.',
        });
        return null;
      }

      return { ...item, categoria_tipo_pedido: categoria };
    },
    confirmarCategoriaItemCliente(item) {
      if (!this.categoriaClienteAtiva()) return true;
      const categoria = categoriaDaLinha(item);
      const bloqueada = this.categoriaPedidoBloqueada();
      // Revalidação síncrona imediatamente antes da mutação protege contra uma
      // segunda inclusão concluída enquanto categoria/preço eram aguardados.
      if (!categoria || (bloqueada && bloqueada !== categoria)) return false;

      if (!bloqueada) {
        this.categoriaClienteReservada = categoria;
        const lista = this.$store.getters['InfoPedido/GET_TIPO_PEDIDOS_CLIENTE'] || [];
        const opcao = lista.find((tipo) => normalizarTipo(tipo?.value) === categoria);
        const tipoPedido = {
          tipopedidoid: opcao?.value || categoria,
          tipopedidodescricao: opcao?.text || (categoria === 'WC' ? 'Venda Certificação' : 'Venda Padrão'),
        };
        const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
        this.$store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', {
          ...form,
          tipoPedidoCliente: tipoPedido,
          tipoPedido: tipoPedido,
        });
        const formItens = this.$store.getters['intensPedido/getFormData'] || {};
        this.$store.commit('intensPedido/SET_FORM_DATA', {
          ...formItens,
          dataCapaPedido: {
            ...(formItens.dataCapaPedido || {}),
            tipopedidoid: categoria,
          },
        });
      }
      return true;
    },
    liberarCategoriaClienteSeVazio() {
      if (normalizarTipo(this.categoriaPedidoPersistida())) return;
      if (this._linhasCategoriaPedido().length === 0) {
        this.categoriaClienteReservada = '';
      }
    },
  },
};